import type { Font } from 'opentype.js';
import { getStroke, type StrokeOptions } from 'perfect-freehand';

const STROKE_OPTIONS: StrokeOptions = {
  size: 6,
  smoothing: 0.46,
  thinning: 0.73,
  streamline: 0.5,
};

const COORDINATE_PRECISION = 1;

export const EMPTY_PATH = '';
export const SIGNATURE_PAD_WIDTH = 640;
export const SIGNATURE_PAD_HEIGHT = 200;
export const SIGNATURE_PAD_ASPECT_RATIO = SIGNATURE_PAD_WIDTH / SIGNATURE_PAD_HEIGHT;
export const SIGNATURE_VIEW_BOX = `0 0 ${SIGNATURE_PAD_WIDTH} ${SIGNATURE_PAD_HEIGHT}`;
export const STROKE_TEST_ID = 'signature-stroke';
export const LIVE_STROKE_TEST_ID = 'signature-live-stroke';
export const TYPED_NAME_TEST_ID = 'signature-typed-name';

export const TYPED_NAME_MAX_LENGTH = 100;

export const SIGNATURE_MIME_TYPE = 'image/svg+xml';
export const SIGNATURE_FILENAME_SUFFIX = 'digital-signature.svg';

const TYPED_NAME_FONT_SIZE = 48;
const TYPED_NAME_MIN_FONT_SIZE = 12;
const TYPED_NAME_SIDE_MARGIN = 24;
const TYPED_NAME_MAX_WIDTH = SIGNATURE_PAD_WIDTH - TYPED_NAME_SIDE_MARGIN * 2;

export const DRAW_MODE = 'draw';
export const TYPE_MODE = 'type';

export type TSignatureMode = typeof DRAW_MODE | typeof TYPE_MODE;
export type TPoint = [number, number];
export type TStroke = TPoint[];

const round = (value: number) => Number(value.toFixed(COORDINATE_PRECISION));

export const toStrokePath = (stroke: TStroke) => {
  const outline = getStroke(stroke, STROKE_OPTIONS);

  if (!outline.length) {
    return EMPTY_PATH;
  }

  const curves = outline.map(([x, y], index) => {
    const [nextX, nextY] = outline[(index + 1) % outline.length];

    return `${round(x)} ${round(y)} ${round((x + nextX) / 2)} ${round((y + nextY) / 2)}`;
  });

  return `M ${curves[0]} Q ${curves.slice(1).join(' ')} Z`;
};

const baselineOf = (font: Font, fontSize: number) => {
  const scale = fontSize / font.unitsPerEm;
  const above = font.ascender * scale;
  const below = -font.descender * scale;

  return (SIGNATURE_PAD_HEIGHT - above - below) / 2 + above;
};

const startOf = (font: Font, name: string, fontSize: number) => {
  const width = font.getAdvanceWidth(name, fontSize);

  return (SIGNATURE_PAD_WIDTH - width) / 2;
};

const fontSizeFor = (font: Font, name: string) => {
  const width = font.getAdvanceWidth(name, TYPED_NAME_FONT_SIZE);

  if (width <= TYPED_NAME_MAX_WIDTH) {
    return TYPED_NAME_FONT_SIZE;
  }

  const fitted = TYPED_NAME_FONT_SIZE * (TYPED_NAME_MAX_WIDTH / width);

  return Math.max(fitted, TYPED_NAME_MIN_FONT_SIZE);
};

export const toTypedNamePath = (font: Font, name: string) => {
  const fontSize = fontSizeFor(font, name);

  return font
    .getPath(name, startOf(font, name, fontSize), baselineOf(font, fontSize), fontSize)
    .toPathData(COORDINATE_PRECISION);
};

export const toSvgMarkup = (svg: SVGSVGElement) => {
  const exported = svg.cloneNode(true) as SVGSVGElement;

  exported.setAttribute('width', String(SIGNATURE_PAD_WIDTH));
  exported.setAttribute('height', String(SIGNATURE_PAD_HEIGHT));
  exported.removeAttribute('style');

  return new XMLSerializer().serializeToString(exported);
};

export const toSignatureFilename = (timestamp: number) =>
  `${timestamp}-${SIGNATURE_FILENAME_SUFFIX}`;

export const toSignatureFile = (markup: string, timestamp: number) =>
  new File([markup], toSignatureFilename(timestamp), { type: SIGNATURE_MIME_TYPE });
